// The supplier content model: the artisan, workshop or house that actually runs
// the experiences, as one record.
//
// It exists for one thing, and the shape follows from it. Ten experiences can be
// run by the same tea house, and when that house closes for a week somebody had
// to open ten experiences and type the same dates ten times — a job nobody
// finishes, so the tenth experience keeps offering a day the house is shut. The
// absences are therefore recorded once here, and every experience linked to this
// supplier inherits them.
//
// A supplier is not a partner. A partner is a travel company that books; a
// supplier is who the booking is with. Neither one signs in as the other, and
// suppliers have no accounts at all for now — the record is maintained by Eighty
// Days, and nobody logs in as a tea house.
//
// Nothing here is a secret in the way a partner's accounting details are, which
// is what lets the supplier document be read by any member: an experience page
// has to say which days the artisan is away, and it reads that from this record
// rather than from a copy. Keep it that way — a phone number or a rate for the
// artisan does not belong on this document while the read rule stays as it is.

import { compareClosurePeriods, sortClosurePeriods, type ClosurePeriodType } from './types_closure';
import type { HistoryInfoType } from './types_history';

// Firestore collection name.
export const coll_suppliers = 'suppliers';

// Archived edit-history steps, written by the `suppliers_written` Cloud
// Function. The name is derived server-side as `{collection} + 'history'`, so
// the two must be kept in step.
export const coll_suppliersHistory = coll_suppliers + 'history';

export interface SupplierType {
  id: string;

  /** How the supplier is referred to in the CMS. Never shown to a guest. */
  name: string;

  /**
   * Anything an admin needs to remember about working with them — who to ring,
   * how much notice they want. Internal: partners never read this document
   * looking for it, and it is not rendered on any public page.
   */
  notes: string;

  /**
   * The days the artisan is away, keyed by an id minted when the row is added.
   *
   * A map rather than a list because a single absence must be addressable: an
   * admin adding next August's holiday writes `closurePeriods.{id}` alone, and
   * two admins working on the same supplier keep each other's rows instead of
   * the last write winning with a rewritten array. `normaliseSupplierClosures`
   * puts it back in calendar order for everything that reads it.
   *
   * Weekday closures are deliberately absent. "Shut on Mondays" is nearly always
   * a fact about the place the experience visits rather than about the artisan,
   * and it stays on the experience where it can differ between them.
   */
  closurePeriods: Record<string, ClosurePeriodType>;

  createdAt?: string;
  updatedAt?: string;

  // Persisted edit history, populated by autosave. Absent until the first edit.
  history?: HistoryInfoType;
}

/**
 * A stored supplier, with anything added to the type since it was written filled
 * in from the defaults. The counterpart of `normaliseExperience` — see there for
 * why a cast is not enough, and why defaulting here writes nothing back.
 */
export function normaliseSupplier(data: Partial<SupplierType>, id: string): SupplierType {
  return { ...makeEmptySupplier(), ...data, id };
}

export function makeEmptySupplier(): SupplierType {
  return {
    id: '',
    name: '',
    notes: '',
    closurePeriods: {},
  };
}

/** A blank absence under a fresh id, for the "add closure" form to fill in. */
export function makeSupplierClosureId(): string {
  return crypto.randomUUID();
}

/**
 * The supplier's absences as a list, in calendar order.
 *
 * Every reader wants the list — the merge into an experience's closures, the
 * editor, the summary on the experience form — so they all come through here
 * rather than each calling `Object.values` and sorting differently. Takes the
 * supplier or nothing, because most callers are holding a lookup that may not
 * have arrived (or may name a supplier since deleted), and "no supplier" and "no
 * absences" mean the same thing to all of them.
 */
export function normaliseSupplierClosures(supplier: SupplierType | undefined): ClosurePeriodType[] {
  if (!supplier) return [];
  return sortClosurePeriods(Object.values(supplier.closurePeriods));
}

/** The rows as `[id, period]` pairs, in the same order. For the editor, which addresses them by id. */
export function supplierClosureEntries(supplier: SupplierType): { id: string; period: ClosurePeriodType }[] {
  return Object.entries(supplier.closurePeriods)
    .map(([id, period]) => ({ id, period }))
    .sort((a, b) => compareClosurePeriods(a.period, b.period));
}

/** Alphabetical by name, which is the only order a supplier list has. */
export function compareSuppliers(a: SupplierType, b: SupplierType): number {
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
}
